import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { catchError, from, map, mergeMap, Observable, switchMap, throwError } from 'rxjs'
import {
  ClockInClockOut,
  ClockInClockOutResponse,
  LopDaysByEmployees
} from '../store/clockIn-clockOut/clockIn-clockOut.state'
import { Action } from '@ngrx/store'
import { eps } from './url.service'
import { UtilsService } from './utils.service'
import { environment } from '../../environments/environment'

export interface ClockInOutCountResponse {
  attendanceDetails: {
    onTime: number
    lateAttendance: number
    earlyLeave: number
    workFromHome: number
    absent: number
    sickLeave: number
    workedDays: number
    performance: string
  }
  leaveDetails: {
    totalLeaves: number
    taken: number
    absent: number
    request: number
    lossOfPay: number
    upcomingLeaves: number
  }
  forceRefresh: boolean
}

@Injectable({
  providedIn: 'root'
})
export class ClockInClockOutService {
  private apiUrl = eps.clockInOut.clockInOutApi

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService
  ) {}

  getAttendances(
    p: {
      range?: { fromDate: Date; toDate: Date }
      filter?: {
        leaveType?: string
        workStatus?: string
        userId?: string
        businessId?: string
        search?: string
      }
      pagination?: { page?: number; limit?: number }
    } & Action<'[Attendances] Load Attendances'>
  ): Observable<{
    totalPresentToday: number
    lateLoginsToday: number
    todayPlannedLeaves: number
    todayUnplannedLeaves: number
    absentToday: number
    employeeCount: number
    totalCount: number
    clockInOuts: ClockInClockOutResponse[]
  }> {
    const range = p?.range
    const filter = p?.filter
    const pagination = p?.pagination
    let params = new HttpParams()
    if (range) {
      params = params.set('fromDate', range.fromDate.toISOString())
      params = params.set('toDate', range.toDate.toISOString())
    }
    if (filter?.leaveType) params = params.set('leaveType', filter.leaveType)
    if (filter?.workStatus) params = params.set('workStatus', filter.workStatus)
    if (filter?.userId) params = params.set('userId', filter.userId)
    if (filter?.businessId) params = params.set('businessId', filter.businessId)
    if (pagination?.page) params = params.set('page', pagination.page.toString())
    if (pagination?.limit) params = params.set('limit', pagination.limit.toString())
    if (filter?.search) params = params.set('search', filter.search)

    return this.http.get<{
      totalPresentToday: number
      lateLoginsToday: number
      todayPlannedLeaves: number
      todayUnplannedLeaves: number
      absentToday: number
      employeeCount: number
      totalCount: number
      clockInOuts: ClockInClockOutResponse[]
    }>(this.apiUrl, {
      params
    })
  }

  // createClockInOut(clockIn: { clockIn: Date }): Observable<ClockInClockOut> {
  //   return this.http.post<ClockInClockOut>(`${this.apiUrl}`, clockIn)
  // }
  // createClockInOut(clockIn: { clockIn: Date }): Observable<ClockInClockOut> {
  //   return this.http.post<ClockInClockOut>(`${this.apiUrl}`, clockIn)
  //   // .pipe(map(response => response.data))
  // }

  //5 dec 2025 ip, lat long
  createClockInOut(payload: { clockIn: Date }): Observable<ClockInClockOut> {
    const deviceInfo = this.utilsService.getDeviceInfo()
    const deviceToken = localStorage.getItem('fcmToken') || this.utilsService.generateToken() || ''

    return from(this.utilsService.getLocation()).pipe(
      switchMap(location => {
        const newPayload = {
          ...payload,
          deviceType: deviceInfo.deviceType,
          deviceInfo: `${deviceInfo.platform} - ${deviceInfo.userAgent}`,
          deviceToken,
          clockinLatitude: location?.latitude ?? null,
          clockinLongitude: location?.longitude ?? null
        }

        return this.http.post<ClockInClockOut>(`${this.apiUrl}`, newPayload)
      })
    )
  }

  // Method to get clock-in data by ID
  getClockInClockOutById(id: string): Observable<ClockInClockOut> {
    return this.http.get<ClockInClockOut>(`${this.apiUrl}/${id}`)
  }

  // updateClockIn(id: string): Observable<ClockInClockOut> {
  //   return this.http.put<ClockInClockOut>(`${this.apiUrl}/${id}`, {})
  //   // .pipe(map(response => response.data))
  // }
  // updateClockIn(id: string): Observable<ClockInClockOut> {
  //   return this.http.put<ClockInClockOut>(`${this.apiUrl}/${id}`, {}).pipe(
  //     catchError(error => {
  //       // Log the error message or handle it as needed
  //       // console.error('Error updating clock-in/clock-out:', error)

  //       // Check if the error has a message and rethrow it with the message
  //       if (error.error && error.error.message) {
  //         return throwError(() => new Error(error.error.message))
  //       } else {
  //         return throwError(() => new Error('Unknown error occurred'))
  //       }
  //     })
  //   )
  // }

  updateClockIn(id: string): Observable<ClockInClockOut> {
    const deviceInfo = this.utilsService.getDeviceInfo()
    const deviceToken = localStorage.getItem('fcmToken') || this.utilsService.generateToken() || ''

    return from(this.utilsService.getLocation()).pipe(
      switchMap(location => {
        const newPayload = {
          deviceType: deviceInfo.deviceType,
          deviceInfo: `${deviceInfo.platform} - ${deviceInfo.userAgent}`,
          deviceToken,
          clockoutLatitude: location?.latitude ?? null,
          clockoutLongitude: location?.longitude ?? null
        }

        return this.http.put<ClockInClockOut>(`${this.apiUrl}/${id}`, newPayload)
      })
    )
  }

  getEmployeeAttendance(
    id: string | null | undefined,
    params: string | null
    // page: number | null | undefined,
    // limit: number | null | undefined
  ): Observable<any> {
    const url = params
      ? `${this.apiUrl}/employeeAttendance/${id}?${params}`
      : `${this.apiUrl}/employeeAttendance/${id}`
    return this.http.get<any>(url)
  }

  getEmployeeLatestClockInOut(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/employeeLatestClockInOut/${id}`)
  }

  getLopDaysForEmployees(
    payPeriodStart: string,
    payPeriodEnd: string,
    businessId: string
  ): Observable<LopDaysByEmployees[]> {
    let params = new HttpParams()
    params = params.set('payPeriodStart', payPeriodStart)
    params = params.set('payPeriodEnd', payPeriodEnd)
    params = params.set('businessId', businessId)

    return this.http.get<LopDaysByEmployees[]>(`${this.apiUrl}/lops`, { params })
  }

  createEvent(payload: {
    clockIn: Date
    clockOut: Date
    workStatus: string
    lateLogin: string | null
    userId: string
    timeZone: string
  }): Observable<ClockInClockOut> {
    return this.http.post<ClockInClockOut>(`${this.apiUrl}/createEvent`, payload)
    // .pipe(map(response => response.data))
  }

  getTodayClockIns(): Observable<any> {
    return this.http.get(`${this.apiUrl}/today`)
  }

  upcomingHoliday(userId: string): Observable<{ title: string; date: string; id: string }> {
    return this.http.get<{ title: string; date: string; id: string }>(
      `${environment.apiBaseUrl}/clockInOut/upcoming-holiday/${userId}`
    )
  }

  getClockInOutCount(
    type?: string,
    fromDate?: string,
    toDate?: string
  ): Observable<ClockInOutCountResponse> {
    const params: any = {}
    if (type) params.type = type
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    return this.http.get<ClockInOutCountResponse>(
      `${environment.apiBaseUrl}/clockInOut/dashboard/attendance-leave-count`,
      { params }
    )
  }

  deleteEvent(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, {})
  }

  getAdminAttendances(
    p: {
      // range?: { fromDate: Date; toDate: Date }
      range?: { toDate: Date }
      filter?: {
        leaveType?: string
        workStatus?: string
        userId?: string
        businessId?: string
        search?: string
      }
      pagination?: { page?: number; limit?: number }
    } & Action<'[Admin Attendances] Load Admin Attendances'>
  ): Observable<{
    totalPresentToday: number
    lateLoginsToday: number
    todayPlannedLeaves: number
    todayUnplannedLeaves: number
    absentToday: number
    employeeCount: number
    totalCount: number
    clockInOuts: ClockInClockOutResponse[]
  }> {
    const range = p?.range
    const filter = p?.filter
    const pagination = p?.pagination
    let params = new HttpParams()
    if (range) {
      // params = params.set('fromDate', range.fromDate.toISOString())
      params = params.set('toDate', range.toDate.toISOString())
    }
    if (filter?.leaveType) params = params.set('leaveType', filter.leaveType)
    if (filter?.workStatus) params = params.set('workStatus', filter.workStatus)
    if (filter?.userId) params = params.set('userId', filter.userId)
    if (filter?.businessId) params = params.set('businessId', filter.businessId)
    if (pagination?.page) params = params.set('page', pagination.page.toString())
    if (pagination?.limit) params = params.set('limit', pagination.limit.toString())
    if (filter?.search) params = params.set('search', filter.search)

    return this.http.get<{
      totalPresentToday: number
      lateLoginsToday: number
      todayPlannedLeaves: number
      todayUnplannedLeaves: number
      absentToday: number
      employeeCount: number
      totalCount: number
      clockInOuts: ClockInClockOutResponse[]
    }>(`${this.apiUrl}/adminAttendance`, {
      params
    })
  }
}
